import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';
import mapboxgl from 'mapbox-gl';
import MapboxLanguage from '@mapbox/mapbox-gl-language';
import * as turf from '@turf/turf';
import { fetchRequest } from '@tk/utilities/tk.fetch';

interface MapMarker {
    lng: number;
    lat: number;
    name: string;
    popupcontent: string;
    markercolor?: string;
    imagepath?: string;
}

export default class DIWMap extends TKCustomElementFactory {
    private map?: mapboxgl.Map;
    private markers: mapboxgl.Marker[] = [];
    private posMarker?: mapboxgl.Marker;
    private mapBounds: mapboxgl.LngLatBounds;
    private container?: HTMLElement;
    private dataUrl?: string;
    private mapStyle: string = 'streets-v12';
    private defaultZoom: number = 8;
    private defaultCenter: [number, number] = [8.247973475979176, 47.0905611674415];
    private ownMarkerModifier: string = 'marker--own';
    private defaultMarkerModifier: string = 'marker--dealer';
    private loaderClassName: string;
    private boundsPadding: number = 50;
    private radiusSliderSelector?: string;
    private travelTimeSliderSelector?: string;
    private showOwnPos: boolean;
    private loader?: HTMLElement;
    private mapboxApiKey?: string;

    constructor() {
        super();
        this.mapBounds = new mapboxgl.LngLatBounds();
        this.container = this.querySelector('[data-tk-map-container]') || undefined;

        this.dataUrl = this.getAttribute('data-tk-map-data-url') || undefined;
        this.mapStyle = this.getAttribute('data-tk-map-style') || 'streets-v12';
        this.defaultZoom = parseInt(this.getAttribute('data-tk-map-zoom') || '8');
        this.radiusSliderSelector = this.getAttribute('data-tk-radius-slider-id') || undefined;
        this.travelTimeSliderSelector = this.getAttribute('data-tk-traveltime-slider-id') || undefined;
        this.showOwnPos = Number(this.getAttribute('data-tk-own-pos')) > 0;
        this.mapboxApiKey = this.getAttribute('data-tk-api-key') || undefined;
        this.loaderClassName = this.getAttribute('data-tk-loader-class') || 'map__loader';

        const defaultLng = this.getAttribute('data-tk-default-center-lng');
        const defaultLat = this.getAttribute('data-tk-default-center-lat');

        if (defaultLng && defaultLat) {
            this.defaultCenter = [parseFloat(defaultLng), parseFloat(defaultLat)];
        }

        if (!this.mapboxApiKey) throw new Error('DIWMap: No API-Key provided');
        if (!this.container) throw new Error('DIWMap: container not found');
    }

    connectedCallback(): void {
        super.connectedCallback();
        this.initMap();
    }

    private initMap(): void {
        // Set Mapbox access token
        mapboxgl.accessToken = this.mapboxApiKey;

        // Create loader overlay
        this.loader = document.createElement('div');
        this.loader.className = this.loaderClassName;
        this.container!.appendChild(this.loader);
        this.showLoader();

        // Initialize map
        this.map = new mapboxgl.Map({
            container: this.container!,
            style: `mapbox://styles/mapbox/${this.mapStyle}`,
            zoom: this.defaultZoom,
            center: this.defaultCenter,
        });

        // Add controls
        this.map.addControl(new mapboxgl.FullscreenControl());
        this.map.addControl(new mapboxgl.NavigationControl());
        this.map.addControl(
            new MapboxLanguage({
                defaultLanguage: 'de',
            }),
        );
        this.map.addControl(
            new mapboxgl.GeolocateControl({
                positionOptions: {
                    enableHighAccuracy: true,
                },
                trackUserLocation: true,
                showUserHeading: false,
            }),
        );

        // Load data if URL provided
        if (this.dataUrl) {
            fetchRequest({
                requestURL: this.dataUrl,
                resolveHandler: this.addMarkers.bind(this),
            });
        } else {
            this.map.on('load', () => {
                this.hideLoader();
                this.dispatchEvent(new CustomEvent('map-loaded', { detail: { map: this.map } }));
            });
        }

        if (this.showOwnPos) {
            this.addOwnPos();
        }

        this.attachEventListeners();
    }

    private addMarkers(response: TKResponse<{ items: MapMarker[] }>): void {
        const data: MapMarker[] = response.dataAsJson.items;

        for (const item of data) {
            if (item.lng > 0 && item.lat > 0) {
                const marker = this.addMarker(item.lng, item.lat, this.defaultMarkerModifier, false, item.popupcontent);

                this.markers.push(marker);
                this.mapBounds.extend([parseFloat(item.lng.toString()), parseFloat(item.lat.toString())]);
            }
        }

        this.hideLoader();
        this.map?.on('load', () => {
            this.dispatchEvent(new CustomEvent('map-loaded', { detail: { map: this.map } }));
        });
    }

    private addMarker(
        lng: number,
        lat: number,
        modifier: string = this.defaultMarkerModifier,
        draggable: boolean = false,
        popupContent: string = '',
    ): mapboxgl.Marker {
        const element = document.createElement('div');
        element.className = `marker ${modifier}`;
        element.innerHTML = `<i class="tk-icon-pin"></i>`;

        const marker = new mapboxgl.Marker({
            draggable: draggable,
            element: element,
        })
            .setLngLat([lng, lat])
            .addTo(this.map!);

        if (popupContent) {
            const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(popupContent);
            marker.setPopup(popup);
        }

        this.mapBounds.extend([parseFloat(lng.toString()), parseFloat(lat.toString())]);

        return marker;
    }

    private addOwnPos(): void {
        this.getUserPosition((lngLat, success) => {
            if (success) {
                this.addPosMarker(lngLat.lng, lngLat.lat);
                this.mapBounds.extend([parseFloat(lngLat.lng.toString()), parseFloat(lngLat.lat.toString())]);
            }
        });
    }

    private getUserPosition(callback: (lngLat: { lng: number; lat: number }, success: boolean) => void): void {
        const lngLat = { lng: this.defaultCenter[0], lat: this.defaultCenter[1] };

        if (!navigator.geolocation) {
            console.error('Geolocation not supported');
            callback(lngLat, false);
            return;
        }

        navigator.permissions.query({ name: 'geolocation' as PermissionName }).then((result) => {
            const posControlsContainer = document.querySelector('[data-poistion-controls]');
            const markerOwnPos = document.querySelector('[data-marker-own-pos]');

            if (result.state === 'granted') {
                posControlsContainer?.removeAttribute('hidden');
                markerOwnPos?.classList.remove('hide-element');
            }
        });

        navigator.geolocation.getCurrentPosition(
            (position) => {
                lngLat.lng = parseFloat(position.coords.longitude.toString());
                lngLat.lat = parseFloat(position.coords.latitude.toString());
                callback(lngLat, true);
            },
            (error) => {
                console.error('Error getting position:', error.message);
                callback(lngLat, false);
            },
        );
    }

    private addPosMarker(lng: number, lat: number): void {
        if (!this.posMarker) {
            this.posMarker = this.addMarker(
                lng,
                lat,
                this.ownMarkerModifier,
                false,
                window.opacc.tkMapMessages?.ownPosition.text,
            );
            this.showRadius();
            this.showIsoChrone();
        } else {
            this.posMarker.setLngLat([parseFloat(lng.toString()), parseFloat(lat.toString())]);
            this.showRadius();
            this.showIsoChrone();
        }
    }

    private flyTo(lng: number, lat: number, zoom: number = 10): void {
        this.map?.flyTo({
            center: [lng, lat],
            essential: true,
            zoom: zoom,
        });
    }

    private attachEventListeners(): void {
        // Radius slider
        if (this.radiusSliderSelector) {
            const radiusSlider = document.getElementById(this.radiusSliderSelector) as HTMLInputElement;
            radiusSlider?.addEventListener('change', (e) => {
                this.onSlideRadius(e as Event);
                this.showRadius();
            });
            radiusSlider?.addEventListener('input', (e) => {
                this.onSlideRadius(e as Event);
            });
        }

        // Travel time slider
        if (this.travelTimeSliderSelector) {
            const travelTimeSlider = document.getElementById(this.travelTimeSliderSelector) as HTMLInputElement;
            travelTimeSlider?.addEventListener('change', (e) => {
                this.onSlideTravelTime(e as Event);
                this.showIsoChrone();
            });
            travelTimeSlider?.addEventListener('input', (e) => {
                this.onSlideTravelTime(e as Event);
            });
        }
    }

    private showRadius(): void {
        if (!this.posMarker || !this.radiusSliderSelector) return;

        const radiusSlider = document.getElementById(this.radiusSliderSelector) as HTMLInputElement;
        if (!radiusSlider) return;

        const valueDisplay = radiusSlider.parentNode?.querySelector('.slider-value');
        if (valueDisplay) {
            valueDisplay.textContent = radiusSlider.value;
        }

        const radiusValue = parseFloat(radiusSlider.value);

        if (radiusValue > 0 && this.posMarker) {
            const lngLat = this.posMarker.getLngLat();
            const circle = turf.circle([lngLat.lng, lngLat.lat], radiusValue, { steps: 100, units: 'kilometers' });

            this.hideAllMarkers();

            // Remove existing circle (layers first, then source)
            if (this.map?.getLayer('circle-outline')) {
                this.map.removeLayer('circle-outline');
            }
            if (this.map?.getLayer('circle')) {
                this.map.removeLayer('circle');
            }
            if (this.map?.getSource('circle')) {
                this.map.removeSource('circle');
            }

            // Add new circle
            this.map?.addSource('circle', {
                type: 'geojson',
                data: circle as GeoJSON.Feature<GeoJSON.Geometry>,
            });

            this.map?.addLayer({
                id: 'circle',
                type: 'fill',
                source: 'circle',
                paint: {
                    'fill-color': '#007bb8',
                    'fill-opacity': 0.1,
                },
            });

            this.map?.addLayer({
                id: 'circle-outline',
                type: 'line',
                source: 'circle',
                paint: {
                    'line-color': '#007bb8',
                    'line-width': 2,
                },
            });

            // Fit map to circle bounds
            const bbox = turf.bbox(circle);
            this.map?.fitBounds(
                [
                    [bbox[0], bbox[1]],
                    [bbox[2], bbox[3]],
                ],
                { padding: this.boundsPadding },
            );

            // Show markers within radius
            this.showMarkers(circle, false);
        } else {
            // Remove circle if radius is 0
            if (this.map?.getLayer('circle-outline')) {
                this.map.removeLayer('circle-outline');
            }
            if (this.map?.getLayer('circle')) {
                this.map.removeLayer('circle');
            }
            if (this.map?.getSource('circle')) {
                this.map.removeSource('circle');
            }
            this.showMarkers(null, false);
        }
    }

    private async showIsoChrone(): Promise<void> {
        if (!this.travelTimeSliderSelector || !this.posMarker) return;

        const travelTimeSlider = document.getElementById(this.travelTimeSliderSelector) as HTMLInputElement;
        if (!travelTimeSlider) return;

        const minutes = parseInt(travelTimeSlider.value);

        if (minutes > 0) {
            this.showLoader();

            const lngLat = this.posMarker.getLngLat();
            const url = `https://api.mapbox.com/isochrone/v1/mapbox/driving/${lngLat.lng},${lngLat.lat}?contours_minutes=${minutes}&polygons=true&access_token=${this.mapboxApiKey}`;

            try {
                const response = await fetch(url);
                const data = await response.json();

                if (data && data.features && data.features.length > 0) {
                    this.hideAllMarkers();

                    // Remove existing isochrone
                    if (this.map?.getLayer('iso')) {
                        this.map.removeLayer('iso');
                    }
                    if (this.map?.getSource('iso')) {
                        this.map.removeSource('iso');
                    }

                    // Add new isochrone
                    this.map?.addSource('iso', {
                        type: 'geojson',
                        data: data,
                    });

                    this.map?.addLayer({
                        id: 'iso',
                        type: 'fill',
                        source: 'iso',
                        paint: {
                            'fill-color': '#692b4e',
                            'fill-opacity': 0.3,
                        },
                    });

                    // Fit map to isochrone bounds
                    const bbox = turf.bbox(data.features[0]);
                    this.map?.fitBounds(
                        [
                            [bbox[0], bbox[1]],
                            [bbox[2], bbox[3]],
                        ],
                        { padding: this.boundsPadding },
                    );

                    // Show markers within isochrone
                    this.showMarkers(data.features[0], false);
                }

                this.hideLoader();
            } catch (error) {
                console.error('Error fetching isochrone:', error);
                this.hideLoader();
            }
        } else {
            // Remove isochrone if minutes is 0
            if (this.map?.getLayer('iso')) {
                this.map.removeLayer('iso');
            }
            if (this.map?.getSource('iso')) {
                this.map.removeSource('iso');
            }
        }
    }

    private hideAllMarkers(): void {
        for (const marker of this.markers) {
            marker.getElement().style.visibility = 'hidden';
        }
    }

    private showMarkers(polygon: GeoJSON.Feature<GeoJSON.Geometry> | null, fitBounds: boolean = false): void {
        if (fitBounds && this.posMarker) {
            this.mapBounds = new mapboxgl.LngLatBounds();
            const lngLat = this.posMarker.getLngLat();
            this.mapBounds.extend([lngLat.lng, lngLat.lat]);
        }

        for (const marker of this.markers) {
            const lngLat = marker.getLngLat();

            if (polygon) {
                const point = turf.point([lngLat.lng, lngLat.lat]);
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                if (turf.booleanPointInPolygon(point, polygon as any)) {
                    marker.getElement().style.visibility = 'visible';
                    if (fitBounds) {
                        this.mapBounds.extend([lngLat.lng, lngLat.lat]);
                    }
                }
            } else {
                marker.getElement().style.visibility = 'visible';
                if (fitBounds) {
                    this.mapBounds.extend([lngLat.lng, lngLat.lat]);
                }
            }
        }

        if (fitBounds) {
            this.map?.fitBounds(this.mapBounds, { padding: this.boundsPadding });
        }
    }

    private onSlideRadius(event: Event): void {
        const slider = event.target as HTMLInputElement;
        const valueDisplay = slider.parentNode?.querySelector('.slider-value');
        if (valueDisplay) {
            valueDisplay.textContent = slider.value;
        }
    }

    private onSlideTravelTime(event: Event): void {
        const slider = event.target as HTMLInputElement;
        const valueDisplay = slider.parentNode?.querySelector('.slider-value');
        if (valueDisplay) {
            valueDisplay.textContent = slider.value;
        }
    }

    private showLoader(): void {
        if (this.loader) {
            this.loader.style.display = 'block';
        }

        // Disable controls
        if (this.radiusSliderSelector) {
            const slider = document.getElementById(this.radiusSliderSelector) as HTMLInputElement;
            if (slider) slider.disabled = true;
        }
        if (this.travelTimeSliderSelector) {
            const slider = document.getElementById(this.travelTimeSliderSelector) as HTMLInputElement;
            if (slider) slider.disabled = true;
        }
    }

    private hideLoader(): void {
        if (this.loader) {
            this.loader.style.display = 'none';
        }

        // Enable controls
        if (this.radiusSliderSelector) {
            const slider = document.getElementById(this.radiusSliderSelector) as HTMLInputElement;
            if (slider) slider.disabled = false;
        }
        if (this.travelTimeSliderSelector) {
            const slider = document.getElementById(this.travelTimeSliderSelector) as HTMLInputElement;
            if (slider) slider.disabled = false;
        }
    }

    public updateMapStyle(style: string): void {
        this.mapStyle = style;
        if (this.map) {
            this.map.setStyle(`mapbox://styles/mapbox/${style}`);
        }
    }
}
